/* ═══ QUIZ TRACKER ═══ */
class QuizTracker {
  constructor() {
    this.sessionId = Math.random().toString(36).substring(2, 10);
    this.startTime = Date.now();
    this.slideTimestamps = {};
    this.currentSlide = 0;
    this.maxSlideReached = 0;
    this.answerChanges = {};
    this.answers = {};
    this.completed = false;
    const cfg = document.getElementById("quiz-tracking-config");
    this.webhookUrl = cfg ? cfg.dataset.webhook : "";
    this.metaPixel = cfg ? cfg.dataset.metaPixel === "true" : false;
    this.ga4 = cfg ? cfg.dataset.ga4 === "true" : false;
    this.variant = cfg ? cfg.dataset.variant : "";
    this.totalSlides = cfg ? parseInt(cfg.dataset.totalSlides) || 0 : 0;
    this.device = /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop";
    this.viewportWidth = window.innerWidth;
    this.referrer = document.referrer || "";
    this.utmParams = this._getUtmParams();
    window.addEventListener("beforeunload", () => { if (!this.completed) this.track("quiz_abandon", { last_slide: this.currentSlide, max_slide_reached: this.maxSlideReached, slides_seen: Object.keys(this.slideTimestamps).length, total_slides: this.totalSlides, time_spent_seconds: Math.round((Date.now() - this.startTime) / 1000), engagement_score: Math.round((this.maxSlideReached / Math.max(this.totalSlides, 1)) * 100) }); });
  }
  _getUtmParams() {
    const p = new URLSearchParams(window.location.search);
    const u = {}; ["utm_source","utm_medium","utm_campaign","utm_content","utm_term"].forEach(k => { if (p.get(k)) u[k] = p.get(k); });
    return u;
  }
  _basePayload() {
    return { session_id: this.sessionId, timestamp: new Date().toISOString(), quiz_variant: this.variant, device: this.device, viewport_width: this.viewportWidth };
  }
  track(eventType, data) {
    const payload = { ...this._basePayload(), event_type: eventType, ...data };
    if (this.webhookUrl) {
      try {
        // Image pixel GET request — no CORS, no redirects, works everywhere
        var img = new Image();
        img.src = this.webhookUrl + "?data=" + encodeURIComponent(JSON.stringify(payload)) + "&t=" + Date.now();
      } catch(e) {}
    }
    if (this.metaPixel && typeof fbq === "function") {
      try { fbq("trackCustom", "Quiz_" + eventType, payload); } catch(e) {}
    }
    if (this.ga4 && typeof gtag === "function") {
      try { gtag("event", "quiz_" + eventType, { ...data, session_id: this.sessionId, quiz_variant: this.variant }); } catch(e) {}
    }
    if (window.console) console.log("[QuizTrack]", eventType, payload);
  }
  trackSlideView(slideIndex, slideType, slideTitle) {
    const now = Date.now();
    const prevSlide = this.currentSlide;
    const timeOnPrev = this.slideTimestamps[prevSlide] ? Math.round((now - this.slideTimestamps[prevSlide]) / 1000) : 0;
    this.currentSlide = slideIndex;
    this.slideTimestamps[slideIndex] = now;
    if (slideIndex > this.maxSlideReached) this.maxSlideReached = slideIndex;
    this.track("slide_view", { slide_index: slideIndex, slide_type: slideType, slide_title: slideTitle || "", prev_slide: prevSlide, time_on_prev_slide_seconds: timeOnPrev, max_slide_reached: this.maxSlideReached });
  }
  trackAnswer(questionIndex, questionText, answerValues, questionType) {
    if (!this.answerChanges[questionIndex]) this.answerChanges[questionIndex] = 0;
    this.answerChanges[questionIndex]++;
    this.answers[questionIndex] = answerValues;
    this.track("answer", { question_index: questionIndex, question_text: questionText, answer_values: answerValues, question_type: questionType, answer_change_count: this.answerChanges[questionIndex], time_on_slide_seconds: this.slideTimestamps[questionIndex] ? Math.round((Date.now() - this.slideTimestamps[questionIndex]) / 1000) : 0 });
  }
  trackStart() { this.track("quiz_start", { page_url: window.location.href, referrer: this.referrer, utm: this.utmParams, total_slides: this.totalSlides }); }
  trackBack(fromSlide, toSlide) { this.track("back_button", { from_slide: fromSlide, to_slide: toSlide }); }
  trackPopup(popupId, response) { this.track("popup_response", { popup_id: popupId, response: response }); }
  trackComplete() {
    this.completed = true;
    this.track("quiz_complete", { total_time_seconds: Math.round((Date.now() - this.startTime) / 1000), total_slides_seen: Object.keys(this.slideTimestamps).length, total_slides: this.totalSlides, max_slide_reached: this.maxSlideReached, engagement_score: 100, all_answers: this.answers });
  }
  trackCTA(url) { this.track("cta_click", { cta_url: url, total_time_seconds: Math.round((Date.now() - this.startTime) / 1000) }); }
  /* NEW: profile assignment tracking */
  trackProfileAssigned(profile, score, breakdown) {
    this.track("profile_assigned", { profile: profile, score: score, score_breakdown: breakdown });
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   OMEN PROFILE SCORING ENGINE
   Reads scoring config from #omen-profile-config div, accumulates
   per-answer weights into a running score, computes profile bucket,
   and rewrites the final CTA href to /pages/.../?profile=X just
   before redirect.
══════════════════════════════════════════════════════════════════════════ */
class OmenProfileScorer {
  constructor() {
    this.score = 0;
    this.breakdown = {}; // { block_id: { answer_index, weight } }
    this.profile = null;

    // Read config from hidden div in section liquid
    const cfg = document.getElementById("omen-profile-config");
    if (!cfg) {
      this.enabled = false;
      console.log("[OmenScorer] No #omen-profile-config — scoring disabled, falling back to default URL.");
      return;
    }
    this.enabled = true;
    this.resultsUrl = cfg.dataset.resultsUrl || "/pages/omen-quiz-pdp";
    this.thresholdAlto = parseInt(cfg.dataset.thresholdAlto) || 26;
    this.thresholdModalto = parseInt(cfg.dataset.thresholdModalto) || 16;

    // Parse weights map: {"block_id": [w1, w2, w3, w4, w5, w6], ...}
    try {
      this.weights = JSON.parse(cfg.dataset.weights || "{}");
    } catch (e) {
      console.error("[OmenScorer] Failed to parse weights JSON", e);
      this.weights = {};
    }

    console.log("[OmenScorer] Initialized", { resultsUrl: this.resultsUrl, thresholdAlto: this.thresholdAlto, thresholdModalto: this.thresholdModalto, weights: this.weights });
  }

  /* Record a single answer change. Re-running on the same block REPLACES
     the previous score for that block (so changing an answer doesn't double-count). */
  recordAnswer(blockId, answerIndex /* 1-based */) {
    if (!this.enabled) return;

    const blockWeights = this.weights[blockId];
    if (!blockWeights || !Array.isArray(blockWeights)) {
      // No weights configured for this block (e.g. interstitial, intro) — ignore
      return;
    }

    const weight = parseInt(blockWeights[answerIndex - 1]) || 0;

    // If this block already had an answer, subtract the old weight first
    if (this.breakdown[blockId]) {
      this.score -= this.breakdown[blockId].weight;
    }

    this.breakdown[blockId] = { answer_index: answerIndex, weight: weight };
    this.score += weight;

    console.log("[OmenScorer] " + blockId + " answer " + answerIndex + " (+" + weight + ") → score=" + this.score);
  }

  /* Resolve final profile from accumulated score */
  resolveProfile() {
    if (!this.enabled) return null;

    if (this.score >= this.thresholdAlto) this.profile = "ALTO";
    else if (this.score >= this.thresholdModalto) this.profile = "MOD-ALTO";
    else this.profile = "MOD";

    console.log("[OmenScorer] Resolved profile: " + this.profile + " (score=" + this.score + ")");
    return this.profile;
  }

  /* Build the final redirect URL with profile + score query params */
  buildResultsUrl() {
    if (!this.enabled) return null;
    if (!this.profile) this.resolveProfile();

    let base;
    try {
      base = new URL(this.resultsUrl, window.location.origin);
    } catch (e) {
      console.error("[OmenScorer] Invalid resultsUrl", this.resultsUrl);
      return null;
    }

    base.searchParams.set("profile", this.profile);
    base.searchParams.set("score", String(this.score));

    // Forward UTM params from quiz entry to PDP for attribution continuity
    const incoming = new URLSearchParams(window.location.search);
    ["utm_source","utm_medium","utm_campaign","utm_content","utm_term","fbclid","gclid"].forEach(k => {
      if (incoming.get(k)) base.searchParams.set(k, incoming.get(k));
    });

    return base.toString();
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   FEAR-BASED POPUP PERSONALIZATION (NEW)
   The last quiz question (quiz_block_q10) asks "Lo que más me
   preocupa es...". Based on which fear they picked (answer 1, 2 or 3),
   we rewrite the text of the 3 confirmation popups that appear during
   the "processing" step, so the popups speak directly to THEIR fear
   instead of generic copy. If they picked "Prefiero no decir" (answer
   4) or didn't answer, the original static popup text (from the
   theme editor) is left untouched.
══════════════════════════════════════════════════════════════════════════ */
const FEAR_POPUP_COPY = {
  1: { // "Que en un año esto sea peor y ya no tenga arreglo"
    first: "Siento que si sigo así, en un año va a ser mucho más difícil de revertir.",
    second: "Me preocupa dejar pasar más tiempo sin hacer nada al respecto.",
    third: "Estoy listo para frenarlo ahora, mientras todavía es simple."
  },
  2: { // "Que la gente lo note antes que yo"
    first: "Siento que otros pueden estar notándolo antes que yo.",
    second: "Me preocupa enterarme por un comentario ajeno.",
    third: "Estoy listo para tomar el control de cómo me ven."
  },
  3: { // "Tener que recurrir a tratamientos caros o invasivos"
    first: "Siento que si lo dejo pasar, después me va a costar mucho más resolverlo.",
    second: "Me preocupa que la única opción termine siendo algo caro o invasivo.",
    third: "Estoy listo para resolverlo por el camino simple, ahora que se puede."
  }
};

/* ═══ QUIZ NAVIGATION ═══ */
if (document.querySelector(".shopify-section.section-quiz")) {
  const quizBlocks = document.querySelectorAll(".shopify-section.section-quiz .quiz-block:not(.quiz-submit-block)");
  const tracker = new QuizTracker();
  const scorer = new OmenProfileScorer();
  window.__omenScorer = scorer; // expuesto para el panel de perfil (Forge)
  var quizStarted = false;
  window.fearAnswerIndex = null; // NEW: tracks which fear (Q10) the person picked, 1-3, or null

  function getSlideInfo(block) {
    return { index: parseInt(block.dataset.index) || 0, type: block.dataset.blockType || "unknown", title: (block.querySelector("h2") || {}).textContent || "" };
  }
  function getSelectedAnswers(block) {
    const checked = block.querySelectorAll("input:checked");
    return Array.from(checked).map(c => c.value);
  }
  /* NEW: derive block ID from data-bloc attr (e.g. "quiz_block_q0") */
  function getBlockId(block) {
    return block.dataset.bloc || block.dataset.blockId || block.id || "";
  }
  /* NEW: derive 1-based answer index from input's position within the block.
     Works regardless of whether the liquid renders the index as an attribute
     or just relies on document order. */
  function getAnswerIndex(input, block) {
    const allInputs = block.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    const idx = Array.prototype.indexOf.call(allInputs, input);
    return idx >= 0 ? idx + 1 : 0;
  }

  // Track first slide view
  var firstBlock = document.querySelector(".shopify-section.section-quiz .quiz-block.visible");
  if (firstBlock) { var fi = getSlideInfo(firstBlock); tracker.trackSlideView(fi.index, fi.type, fi.title); }

  quizBlocks.forEach((block) => {
    const interaction = block.dataset.question_interaction;

    // Track answer selections (for answer + answer_changed events)
    block.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
      input.addEventListener("change", function(e) {
        if (!quizStarted) { quizStarted = true; tracker.trackStart(); }
        var si = getSlideInfo(block);
        var vals = getSelectedAnswers(block);
        var qText = (block.querySelector("h2") || {}).textContent || "";
        tracker.trackAnswer(si.index, qText, vals, input.type);

        /* NEW: feed scorer */
        var blockId = getBlockId(block);
        var answerIdx = getAnswerIndex(input, block);
        if (blockId && answerIdx > 0) {
          scorer.recordAnswer(blockId, answerIdx);
        }

        /* NEW: capture the fear answer (Q10) for popup personalization.
           Only answers 1-3 map to a real fear; 4 = "Prefiero no decir". */
        if (blockId === "quiz_block_q10") {
          window.fearAnswerIndex = (answerIdx >= 1 && answerIdx <= 3) ? answerIdx : null;
        }
      });
    });

    if (interaction === "oninputchange") {
      const inputs = block.querySelectorAll('input[type="radio"]');
      inputs.forEach((input) => {
        input.addEventListener("change", function (e) {
          e.stopPropagation();
          setTimeout(() => nextQuiz(block), 300);
        });
      });
    } else if (interaction === "ctaclick") {
      const btn = block.querySelector(".next_quiz");
      if (btn) {
        btn.addEventListener("click", function (e) {
          e.stopPropagation();
          if (!quizStarted) { quizStarted = true; tracker.trackStart(); }
          const checkboxes = block.querySelectorAll('input[type="checkbox"]:checked');
          const radios = block.querySelectorAll('input[type="radio"]:checked');
          if (block.dataset.blockType === "summery-block" || block.dataset.blockType === "results-block" || block.dataset.blockType === "goal-block" || block.dataset.blockType === "social-proof-block" || block.dataset.blockType === "education-block" || checkboxes.length > 0 || radios.length > 0) {
            if (block.classList.contains("last-block")) {
              submitBlockquestion(block);
            } else {
              nextQuiz(block);
            }
          }
        });
      }
    }
    const prevBtn = block.querySelector(".previous_quiz");
    if (prevBtn) {
      prevBtn.addEventListener("click", function (e) {
        e.stopPropagation();
        var fromInfo = getSlideInfo(block);
        previewQuiz(block);
        var prevBlock = block.previousElementSibling;
        if (prevBlock) { var toInfo = getSlideInfo(prevBlock); tracker.trackBack(fromInfo.index, toInfo.index); }
      });
    }
  });

  // Initialize progress bar
  updateProgress(0);

  function nextQuiz(currentQuizBlock) {
    currentQuizBlock.classList.remove("visible");
    currentQuizBlock.classList.add("hidden");
    const nextQuizBlock = currentQuizBlock.nextElementSibling;
    if (nextQuizBlock && nextQuizBlock.classList.contains("quiz-block")) {
      nextQuizBlock.classList.remove("hidden");
      nextQuizBlock.classList.add("visible");
      updateProgress(parseInt(nextQuizBlock.dataset.index) - 1);
      var si = getSlideInfo(nextQuizBlock);
      tracker.trackSlideView(si.index, si.type, si.title);
    }
  }

  function previewQuiz(currentQuizBlock) {
    currentQuizBlock.classList.remove("visible");
    currentQuizBlock.classList.add("hidden");
    const previousQuizBlock = currentQuizBlock.previousElementSibling;
    if (previousQuizBlock && previousQuizBlock.classList.contains("quiz-block")) {
      previousQuizBlock.classList.remove("hidden");
      previousQuizBlock.classList.add("visible");
      updateProgress(parseInt(previousQuizBlock.dataset.index) - 1);
      var si = getSlideInfo(previousQuizBlock);
      tracker.trackSlideView(si.index, si.type, si.title);
    }
  }

  function updateProgress(totalNumberOfSteps) {
    var progressPercentage = (100 / document.querySelectorAll(".shopify-section.section-quiz .quiz-block").length) * totalNumberOfSteps;
    var topBar = document.getElementById("quiz-top-progress-bar");
    if (topBar) {
      topBar.ariaValueNow = progressPercentage;
      topBar.style.width = progressPercentage + "%";
    }
  }

  function submitBlockquestion(currentQuizBlock) {
    currentQuizBlock.classList.remove("visible");
    currentQuizBlock.classList.add("hidden");
    const nextQuizBlock = currentQuizBlock.nextElementSibling;
    if (nextQuizBlock && nextQuizBlock.classList.contains("quiz-block")) {
      nextQuizBlock.classList.remove("hidden");
      nextQuizBlock.classList.add("visible");
      var topBar = document.getElementById("quiz-top-progress-bar");
      if (topBar) { topBar.style.width = "100%"; topBar.ariaValueNow = 100; }
      tracker.trackSlideView(999, "processing", "Procesando");

      /* NEW: resolve profile NOW so it's ready when the redirect fires */
      if (scorer.enabled) {
        scorer.resolveProfile();
        tracker.trackProfileAssigned(scorer.profile, scorer.score, scorer.breakdown);
      }

      window.firstProgress = new ProgressBar("first");
      window.secondProgress = new ProgressBar("second");
      window.thirdProgress = new ProgressBar("third");
      setTimeout(() => { firstProgress.activate(); firstProgress.startProgress(); }, 100);
    }
  }
}

class ProgressBar {
  constructor(id) {
    this.id = id; this.percentage = 0;
    this.intervalId = null; this.element = document.getElementById(id + "-progress"); this.isComplete = false;
    this.finalTitle = this.element ? (this.element.dataset.completeTitle || id) : id;
  }
  updateProgressBar() {
    const progressPath = this.element.querySelector(".progress-bar .progress");
    progressPath.style.strokeDashoffset = 100 - this.percentage;
    this.element.querySelector(".progress-percentage").textContent = this.percentage + "%";
    if (this.percentage >= 100 && !this.isComplete) this.completeProgress();
  }
  completeProgress() {
    this.isComplete = true;
    this.element.querySelector(".progress-title").textContent = this.finalTitle;
    this.element.querySelector(".progress-percentage").style.display = "none";
    this.element.querySelector(".check-icon").style.display = "block";
    clearInterval(this.intervalId);
    const event = new Event("progressComplete");
    this.element.dispatchEvent(event);
    if (this.id === "third") {
      if (typeof tracker !== "undefined") tracker.trackComplete();
      const targetButton = document.querySelector(".quiz-submit-block .generic-button");
      if (targetButton) {
        /* NEW: rewrite the CTA href to /pages/omen-quiz-pdp?profile=X
           BEFORE the user clicks (and before the direct-redirect fires).
           Falls back to original href if scorer disabled. */
        if (typeof scorer !== "undefined" && scorer.enabled) {
          var dynamicUrl = scorer.buildResultsUrl();
          if (dynamicUrl) {
            targetButton.href = dynamicUrl;
            targetButton.setAttribute("href", dynamicUrl);
            console.log("[OmenScorer] CTA redirect set →", dynamicUrl);
          }
        }

        // OMEN: cerrar la oferta DENTRO del quiz — no redirigir.
        // En vez de mandar a la PDP, mostramos el botón "Ver mi resultado"
        // que despliega el bloque de cierre (#omen-close-wrap).
        var revealBtn = document.getElementById("omen-reveal-btn");
        if (revealBtn) {
          revealBtn.style.display = "block";
          revealBtn.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          // Fallback: si por algún motivo no está el cierre, usar el botón viejo.
          targetButton.classList.remove("hidden");
          setTimeout(() => { targetButton.style.opacity = "1"; }, 10);
        }
      }
    }
  }
  showPopup() {
    const p = document.getElementById(this.id + "Popup");
    const o = document.getElementById(this.id + "PopupOverlay");

    /* NEW: swap in fear-personalized copy if we have a match for this popup + the fear they picked */
    var fearIdx = window.fearAnswerIndex;
    if (fearIdx && FEAR_POPUP_COPY[fearIdx] && FEAR_POPUP_COPY[fearIdx][this.id]) {
      var mainTextEl = p.querySelector(".main-text");
      if (mainTextEl) mainTextEl.textContent = FEAR_POPUP_COPY[fearIdx][this.id];
    }

    p.style.display = "block"; o.style.display = "block";
    p.offsetHeight; p.classList.add("visible"); o.classList.add("visible");
  }
  hidePopup() {
    const p = document.getElementById(this.id + "Popup");
    const o = document.getElementById(this.id + "PopupOverlay");
    p.classList.remove("visible"); o.classList.remove("visible");
    setTimeout(() => { p.style.display = "none"; o.style.display = "none"; }, 300);
  }
  activate() { this.element.classList.add("visible"); }
  startProgress() {
    if (this.intervalId) clearInterval(this.intervalId);
    this.intervalId = setInterval(() => {
      if (this.percentage >= 100) { clearInterval(this.intervalId); this.completeProgress(); return; }
      if (this.percentage === 49) { clearInterval(this.intervalId); this.showPopup(); return; }
      this.percentage++; this.updateProgressBar();
    }, 30);
  }
  continueProgress() { this.hidePopup(); this.percentage++; this.startProgress(); }
}

window.continueProgress = function(which) {
  var popupId = which;
  var btn = event && event.target ? event.target : null;
  var response = btn ? (btn.classList.contains("fPopY") ? "yes" : "no") : "unknown";
  if (typeof tracker !== "undefined") tracker.trackPopup(popupId, response);
  if (which === "first") {
    firstProgress.continueProgress();
    firstProgress.element.addEventListener("progressComplete", () => {
      setTimeout(() => { secondProgress.activate(); secondProgress.startProgress(); }, 1000);
    }, { once: true });
  } else if (which === "second") {
    secondProgress.continueProgress();
    secondProgress.element.addEventListener("progressComplete", () => {
      setTimeout(() => { thirdProgress.activate(); thirdProgress.startProgress(); }, 1000);
    }, { once: true });
  } else { thirdProgress.continueProgress(); }
};

/* ══════════════════════════════════════════════════════════════════════════
   FORGE PROFILE PANEL — rellena el panel de perfil según el score real.
   Se llama desde el onclick del botón "Ver mi resultado".
   Lee window.__omenScorer (seteado abajo) para decidir Alto vs Moderado-alto.
══════════════════════════════════════════════════════════════════════════ */
window.fillForgeProfile = function () {
  try {
    var scorer = window.__omenScorer;
    var profile = "ALTO";
    if (scorer && scorer.enabled) {
      profile = scorer.resolveProfile() || "ALTO";
    }

    var isAlto = (profile === "ALTO");
    // knob position (% del ancho): ALTO cerca del extremo, MOD-ALTO en la zona media-alta
    var knobPos = isAlto ? 88 : 68;
    var levelLabel = isAlto ? "Alto" : "Moderado-alto";
    var marginLabel = isAlto ? "Alto" : "Muy alto";
    var impactLabel = isAlto ? "Notable" : "Moderado";

    var knob = document.getElementById("fp-knob");
    if (knob) knob.style.left = knobPos + "%";

    var badge = document.getElementById("fp-level-badge");
    if (badge) {
      badge.textContent = levelLabel;
      if (!isAlto) badge.classList.add("mod");
    }

    var noteWrap = document.getElementById("fp-note");
    var noteTxt = document.getElementById("fp-note-txt");
    if (noteTxt) {
      if (isAlto) {
        noteTxt.innerHTML = "<b>Nivel ALTO.</b> Un envejecimiento alto lleva a arrugas marcadas, piel flácida y cara de cansado que no se va aunque duermas.";
      } else {
        noteTxt.innerHTML = "<b>Nivel MODERADO-ALTO.</b> Ya hay señales claras de envejecimiento acelerado. Todavía estás a tiempo de revertir gran parte antes de que se marque más.";
        if (noteWrap) noteWrap.classList.add("mod");
      }
    }

    var marginEl = document.getElementById("fp-margin");
    if (marginEl) marginEl.textContent = marginLabel;
    var impactEl = document.getElementById("fp-impact");
    if (impactEl) impactEl.textContent = impactLabel;

    console.log("[ForgePanel] perfil=" + profile + " knob=" + knobPos + "%");
  } catch (e) {
    console.error("[ForgePanel] error rellenando panel", e);
  }
};
